import React, { useState, useEffect, useRef } from 'react';
import { X, UserPlus, Image as ImageIcon, Loader2, Trash2, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCreateOpportunity, useUpdateOpportunity } from '../hooks/use-opportunities';
import { uploadFile } from '../../../lib/uploads';

const TYPES = ['Full Time', 'Part Time', 'Internship', 'Contract'];
const EXPERIENCE_LEVELS = [
  'Entry Level (0-2 years)',
  'Mid Level (2-5 years)',
  'Senior (5+ years)',
  'Any Level',
];

const PostOpportunityModal = ({ isOpen, onClose, mode = 'create', initialData = null }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    clinicName: '',
    location: '',
    type: 'Full Time',
    experienceRequired: 'Entry Level (0-2 years)',
    screeningQuestion: '',
    caseAttachmentUrl: '',   // storage key — submitted to the API
    caseAttachmentPreview: '', // signed URL — thumbnail only, never submitted
    createDiscussionPanel: false,
  });
  const [error, setError] = useState('');
  // Upload starts the moment a file is chosen, so the transfer overlaps with the
  // rest of the form being filled in. Until it finishes the file exists only in
  // a staging prefix that the bucket's lifecycle rule sweeps, so abandoning the
  // form costs a day of storage rather than an orphan.
  const [pendingFile, setPendingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  // Local blob: preview, kept so it can be revoked. The browser leaks the object
  // for the life of the tab otherwise.
  const objectUrlRef = useRef(null);

  const releasePreview = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  useEffect(() => releasePreview, []);

  const { mutate: createOpportunity, isPending: isCreating } = useCreateOpportunity();
  const { mutate: updateOpportunity, isPending: isUpdating } = useUpdateOpportunity();

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setForm({
          title: initialData.title || '',
          clinicName: initialData.clinicName || '',
          location: initialData.location || '',
          type: initialData.type || 'Full Time',
          experienceRequired: initialData.experienceRequired || 'Entry Level (0-2 years)',
          screeningQuestion: initialData.screeningQuestion || '',
          // The API returns a signed URL in caseAttachmentUrl and the raw key in
          // caseAttachmentKey. The form must carry the key — re-submitting a
          // signed URL fails key validation.
          caseAttachmentUrl: initialData.caseAttachmentKey || '',
          caseAttachmentPreview: initialData.caseAttachmentUrl || '',
          createDiscussionPanel: initialData.createDiscussionPanel || false,
        });
      } else {
        setForm({
          title: '',
          clinicName: '',
          location: '',
          type: 'Full Time',
          experienceRequired: 'Entry Level (0-2 years)',
          screeningQuestion: '',
          caseAttachmentUrl: '',
          caseAttachmentPreview: '',
          createDiscussionPanel: false,
        });
      }
      releasePreview();
      setPendingFile(null);
      setIsUploading(false);
      setUploadProgress(0);
      setError('');
    }
  }, [isOpen, mode, initialData]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Let the same file be re-picked after a failure.
    e.target.value = '';

    // Mirrors the server's 1MB cap so the user is told immediately rather than
    // after a round trip. The server is the one that actually enforces it.
    if (file.size > 1 * 1024 * 1024) {
      setError('File size must be less than 1MB');
      return;
    }

    // Show the file straight away from a local blob:, then upload in the
    // background. The key returned at the end is what gets submitted.
    releasePreview();
    const localUrl = URL.createObjectURL(file);
    objectUrlRef.current = localUrl;

    setError('');
    setPendingFile(file);
    setForm((prev) => ({ ...prev, caseAttachmentUrl: '', caseAttachmentPreview: localUrl }));
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadFile(file, {
        purpose: 'opportunity-attachment',
        onProgress: setUploadProgress,
      });
      setForm((prev) => ({
        ...prev,
        // A staging key. It stays under `tmp/` — swept by the bucket lifecycle
        // rule if this form is abandoned — until the service promotes it while
        // writing the row. Nothing to clean up by hand.
        caseAttachmentUrl: result.key,
        // The local blob is the only preview available now: no signed URL exists
        // until the attachment is confirmed, which happens server-side at submit.
        caseAttachmentPreview: localUrl,
      }));
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.response?.data?.message || 'Failed to upload file. Please try again.');
      releasePreview();
      setPendingFile(null);
      setForm((prev) => ({ ...prev, caseAttachmentUrl: '', caseAttachmentPreview: '' }));
    } finally {
      setIsUploading(false);
    }
  };

  const removeAttachment = () => {
    // The uploaded object is left in the staging prefix for the bucket's
    // lifecycle rule — same as Share Case. No request needed to un-upload.
    releasePreview();
    setPendingFile(null);
    setForm((prev) => ({ ...prev, caseAttachmentUrl: '', caseAttachmentPreview: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.clinicName.trim() || !form.location.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    // The attachment is already in B2 by now; only wait if it is still going.
    if (isUploading) {
      setError('Please wait for the attachment to finish uploading.');
      return;
    }

    const attachmentKey = form.caseAttachmentUrl;

    // caseAttachmentPreview is frontend-only — it holds the signed URL for the
    // thumbnail. The API stores the key, which lives in caseAttachmentUrl.
    // Spreading it into the payload reaches Prisma as an unknown column.
    const { caseAttachmentPreview, ...formFields } = form;

    const payload = {
      ...formFields,
      screeningQuestion: form.screeningQuestion.trim() || undefined,
      // Editing has to distinguish "unchanged" from "removed". `undefined` means
      // leave the column alone, so sending it for an emptied attachment would
      // silently keep the old one — the removal appears to work until the next
      // refetch brings the file back. An empty string clears it, and the server
      // reads that as a change and deletes the old object.
      caseAttachmentUrl: mode === 'edit' ? attachmentKey : attachmentKey || undefined,
    };

    if (mode === 'edit') {
      updateOpportunity(
        { id: initialData.id, data: payload },
        {
          onSuccess: () => onClose(),
          onError: (err) => {
            const fieldErrors = err?.response?.data?.errors;
            if (fieldErrors && Object.keys(fieldErrors).length > 0) {
              setError(Object.values(fieldErrors).flat()[0]);
            } else {
              setError(err?.response?.data?.message || 'Failed to update opportunity.');
            }
          },
        }
      );
    } else {
      createOpportunity(
        payload,
        {
          onSuccess: () => {
            setForm({
              title: '',
              clinicName: '',
              location: '',
              type: 'Full Time',
              experienceRequired: 'Entry Level (0-2 years)',
              screeningQuestion: '',
              caseAttachmentUrl: '',
              caseAttachmentPreview: '',
              createDiscussionPanel: false,
            });
            setPendingFile(null);
            onClose();
            navigate('/my-postings');
          },
          onError: (err) => {
            const fieldErrors = err?.response?.data?.errors;
            if (fieldErrors && Object.keys(fieldErrors).length > 0) {
              setError(Object.values(fieldErrors).flat()[0]);
            } else {
              setError(err?.response?.data?.message || 'Failed to post opportunity.');
            }
          },
        }
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fadeIn scrollbar-hide">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <UserPlus className="text-[#6b46c1] mt-0.5" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {mode === 'edit' ? 'Edit Veterinary Position' : 'Post a Veterinary Position'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {mode === 'edit' ? 'Update the details for this position.' : 'Create a job posting and optionally add clinical screening questions to find the right candidate.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 -mt-6 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          
          {/* Section: Job Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800">Job Details</h3>
            
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Role Title <span className="text-red-500">*</span>
                </label>
                <span className={`text-xs ${form.title.length >= 200 ? 'text-red-500 font-bold' : form.title.length >= 175 ? 'text-amber-500 font-semibold' : 'text-gray-400'}`}>
                  {form.title.length}/200
                </span>
              </div>
              <input
                type="text"
                value={form.title}
                maxLength={200}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Veterinary Surgeon, Emergency Veterinarian"
                className="w-full px-4 py-2.5 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Clinic/Hospital Name <span className="text-red-500">*</span>
                  </label>
                  <span className={`text-xs ${form.clinicName.length >= 255 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                    {form.clinicName.length}/255
                  </span>
                </div>
                <input
                  type="text"
                  value={form.clinicName}
                  maxLength={255}
                  onChange={(e) => handleChange('clinicName', e.target.value)}
                  placeholder="Hope Animal Clinic"
                  className="w-full px-4 py-2.5 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <span className={`text-xs ${form.location.length >= 255 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                    {form.location.length}/255
                  </span>
                </div>
                <input
                  type="text"
                  value={form.location}
                  maxLength={255}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="Hyderabad, India"
                  className="w-full px-4 py-2.5 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Experience Required <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.experienceRequired}
                  onChange={(e) => handleChange('experienceRequired', e.target.value)}
                  className="w-full py-2.5 px-3 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
                >
                  <option disabled value="">Select experience level</option>
                  {EXPERIENCE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Employment Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full py-2.5 px-3 text-sm bg-gray-100 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
                >
                  <option disabled value="">Select job type</option>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Section: Clinical Screening */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-[#1e3a8a]">Clinical Screening (Optional)</h3>
              <p className="text-sm text-gray-500 mt-0.5">Ask a clinical question or attach a case to assess candidate's expertise</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#1e3a8a]">
                  Screening Question
                </label>
                <span className={`text-xs ${form.screeningQuestion.length >= 1000 ? 'text-red-500 font-bold' : form.screeningQuestion.length >= 900 ? 'text-amber-500 font-semibold' : 'text-gray-400'}`}>
                  {form.screeningQuestion.length}/1000
                </span>
              </div>
              <textarea
                value={form.screeningQuestion}
                maxLength={1000}
                onChange={(e) => handleChange('screeningQuestion', e.target.value)}
                placeholder="e.g., How would you approach a 5-year-old dog presenting with acute vomiting and bloody diarrhea? Or describe your experience with orthopedic surgery..."
                rows={4}
                className="w-full px-4 py-3 text-sm bg-gray-100 text-gray-600 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent resize-none transition-colors placeholder:text-gray-400 break-words [overflow-wrap:anywhere]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1e3a8a] mb-2">
                Attach Clinical Case (Optional)
              </label>
              
              <input 
                type="file" 
                ref={fileInputRef}
                className="hidden" 
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" 
                onChange={handleFileUpload} 
              />
              
              <div 
                onClick={() => !isUploading && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-5 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
                  isUploading ? 'border-gray-300 bg-gray-50 cursor-wait' : 'border-[#6b46c1]/40 hover:border-[#6b46c1]/70 bg-white'
                }`}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin text-[#6b46c1] mb-2" size={26} />
                    <p className="text-sm font-medium text-[#1e3a8a]">Uploading... {uploadProgress}%</p>
                  </>
                ) : (pendingFile || form.caseAttachmentUrl) ? (
                  <>
                    <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-2">
                      <ImageIcon className="text-green-600" size={24} />
                    </div>
                    <p className="text-sm font-medium text-green-700">File attached successfully</p>
                    <p className="text-xs text-green-600/70 mt-1">Click to replace</p>
                  </>
                ) : (
                  <>
                    <ImageIcon className="text-gray-400 mb-1.5" size={26} />
                    <p className="text-sm font-medium text-[#1e3a8a]">Click to upload case images or documents</p>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, PDF up to 1MB</p>
                  </>
                )}
              </div>

              {/* Uploaded Case Attachment Preview Thumbnail */}
              {/* Renders from caseAttachmentPreview, never from caseAttachmentUrl:
                  that field holds a storage key, which is not a fetchable
                  address, so using it here produced a broken thumbnail and a
                  dead "View Full Attachment" link when editing. The key decides
                  whether to show the block; the preview decides what to show. */}
              {form.caseAttachmentUrl && form.caseAttachmentPreview && (
                <div className="relative mt-3 p-3 bg-[#faf5ff] border border-[#e9d5ff] rounded-xl flex items-center justify-between gap-3 shadow-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    {/\.(jpg|jpeg|png|webp|gif)$/i.test(form.caseAttachmentUrl) ? (
                      <img
                        src={form.caseAttachmentPreview}
                        alt="Attached Clinical Case"
                        className="w-14 h-14 object-cover rounded-lg border border-purple-200 shrink-0 shadow-xs"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center shrink-0">
                        <FileText size={22} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">Clinical Case Attached</p>
                      <a
                        href={form.caseAttachmentPreview}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-purple-700 font-medium hover:underline truncate block mt-0.5"
                      >
                        View Full Attachment ↗
                      </a>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                    title="Remove file"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Discussion Panel toggle */}
          <div className="p-4 bg-[#faf5ff] border border-[#e9d5ff] rounded-lg flex items-start gap-3">
            <div className="pt-0.5">
              <UserPlus className="text-[#8b5cf6]" size={18} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-gray-900">Create as Discussion Panel</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={form.createDiscussionPanel}
                    onChange={(e) => handleChange('createDiscussionPanel', e.target.checked)}
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#a78bfa]"></div>
                </label>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                Enable this to create a dedicated panel for this job posting. Candidates can discuss the role, ask questions, and share their approach to the screening case. Great for collaborative hiring and educational purposes.
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded-lg">{error}</p>
          )}

          <hr className="border-gray-100" />

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-gray-700 hover:text-gray-900 px-2 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || isUpdating || isUploading}
              className="px-6 py-2.5 text-sm font-medium text-white bg-[#a78bfa] rounded-lg hover:bg-[#8b5cf6] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm"
            >
              {/* Deliberately not spinning while the attachment uploads. The
                  drop zone already shows a spinner and a percentage, and a
                  second one here reads as two things happening at once — while
                  saying "Saving..." for work that has not started. The button
                  stays disabled instead. */}
              {isCreating || isUpdating ? (
                <><Loader2 size={16} className="animate-spin" /> {mode === 'edit' ? 'Saving...' : 'Posting...'}</>
              ) : (
                mode === 'edit' ? 'Save Changes' : (form.createDiscussionPanel ? 'Create Panel & Post Job' : 'Post Job')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostOpportunityModal;
